import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type AppLocale = '' | 'en' | 'ja' | 'ko';
export type AppTheme = 'light' | 'dark';
export type LightPalette = 'blue' | 'warm';

interface ExperienceValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  theme: AppTheme;
  setTheme: (theme: AppTheme) => void;
  lightPalette: LightPalette;
  setLightPalette: (palette: LightPalette) => void;
  languagesEnabled: boolean;
  darkModeEnabled: boolean;
  label: (key: string) => string;
}

const UI_LABELS: Record<Exclude<AppLocale, ''>, Record<string, string>> = {
  en: { draw: 'Draw', gallery: 'Gallery', palettes: 'Colors', tutorials: 'Guides', products: 'Products', scan: 'Scan share', light: 'Light', dark: 'Dark' },
  ja: { draw: '描画', gallery: '広場', palettes: '色見本', tutorials: 'ガイド', products: '商品', scan: '共有を読取', light: 'ライト', dark: 'ダーク' },
  ko: { draw: '그리기', gallery: '광장', palettes: '색상표', tutorials: '가이드', products: '상품', scan: '공유 스캔', light: '라이트', dark: '다크' }
};
const DEFAULT_LABELS: Record<string, string> = { draw: '绘制', gallery: '广场', palettes: '色卡', tutorials: '教程', products: '商品', scan: '扫描分享图纸', light: '浅色', dark: '深色' };

const ExperienceContext = createContext<ExperienceValue>({
  locale: '',
  setLocale: () => undefined,
  theme: 'light',
  setTheme: () => undefined,
  lightPalette: 'blue',
  setLightPalette: () => undefined,
  languagesEnabled: false,
  darkModeEnabled: true,
  label: (key) => DEFAULT_LABELS[key] || key
});

export function ExperienceProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<AppLocale>(() => {
    const value = window.localStorage.getItem('yxmpd-locale-v1');
    return value === 'en' || value === 'ja' || value === 'ko' ? value : '';
  });
  const [theme, setThemeState] = useState<AppTheme>(() => window.localStorage.getItem('yxmpd-theme-v1') === 'dark' ? 'dark' : 'light');
  const [lightPalette, setLightPaletteState] = useState<LightPalette>(() => window.localStorage.getItem('yxmpd-light-palette-v1') === 'warm' ? 'warm' : 'blue');
  const [settings, setSettings] = useState({ languagesEnabled: false, darkModeEnabled: true });

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/site-settings', { credentials: 'include', signal: controller.signal, cache: 'no-store' })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        const frontend = data?.settings?.frontend || {};
        setSettings({ languagesEnabled: Boolean(frontend.languagesEnabled), darkModeEnabled: frontend.darkModeEnabled !== false });
        if (!frontend.languagesEnabled) setLocaleState('');
        if (frontend.darkModeEnabled === false) setThemeState('light');
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.lightPalette = lightPalette;
    document.documentElement.lang = locale === 'ja' ? 'ja' : locale === 'ko' ? 'ko' : locale === 'en' ? 'en' : 'zh-CN';
    window.localStorage.setItem('yxmpd-theme-v1', theme);
    window.localStorage.setItem('yxmpd-light-palette-v1', lightPalette);
  }, [lightPalette, locale, theme]);

  const setLocale = (next: AppLocale) => {
    const value = settings.languagesEnabled ? next : '';
    setLocaleState(value);
    window.localStorage.setItem('yxmpd-locale-v1', value);
  };
  const setTheme = (next: AppTheme) => setThemeState(settings.darkModeEnabled ? next : 'light');
  const setLightPalette = (next: LightPalette) => setLightPaletteState(next);
  const value = useMemo<ExperienceValue>(() => ({
    locale,
    setLocale,
    theme,
    setTheme,
    lightPalette,
    setLightPalette,
    languagesEnabled: settings.languagesEnabled,
    darkModeEnabled: settings.darkModeEnabled,
    label: (key) => (locale ? UI_LABELS[locale]?.[key] : DEFAULT_LABELS[key]) || DEFAULT_LABELS[key] || key
  }), [lightPalette, locale, settings.darkModeEnabled, settings.languagesEnabled, theme]);
  return <ExperienceContext.Provider value={value}>{children}</ExperienceContext.Provider>;
}

export function useExperience() { return useContext(ExperienceContext); }
